import React, { useState, useEffect, useRef, useCallback } from "react";
import { X, Zap, LucideIcon, CircleCheck } from "lucide-react";
import { once } from "events";
import VoiceCallInterface from "./voiceInterface";
import aposTropheIcon from "@/assets/invertedcommas.svg";
import { NoAgentNotification } from "@/components/ui/NoAgentNotification";
import { RoomEvent, MediaDeviceFailure, Room } from "livekit-client";
import { useCallStatusPolling } from "@/components/hooks/useCallStatusPolling";

import {
  LiveKitRoom,
  useVoiceAssistant,
  RoomAudioRenderer,
  AgentState,
  useRoomContext
} from "@livekit/components-react";

const TSCAgent = "/tsc-agent.png";

interface LivekitVoiceProps {
  pageCategory: string;
  isDialogOpen: boolean;
  toggleDialog: () => void;
  phoneNumber: string;
  callHints: any[];
  callHintsTable: any[];
  agentName: string;
  features: string[];
  icon: string;
  callTitle: string;
  voiceConfig: {
    baseUrl: string;
    url?: string;
    user_id: string;
    agent_id: string;
    overide_previous_context?: boolean;
  };
  try_speaking: string[] | [];
  activities: string[] | [];
  dynamicVariables: any;
  isFeedbackFormOpen?: boolean;
  ttsConfigId: string | null;
  setIsFeedbackFormOpen?: (isOpen: boolean) => void;
  setConnectionDetails?: (details: any) => void;
}

interface TranscriptMessage {
  id: string;
  speaker: string;
  text: string;
  time: string;
  type?: string;
  timestamp: number;
}

interface SidebarSectionProps {
  title: string;
  children: React.ReactNode;
  className?: string;
}

interface SuggestionItemProps {
  text: string;
  icon?: string;
}

interface HintItemProps {
  text: string;
}

interface FeatureItemProps {
  text: string;
  enabled?: boolean;
}

interface ActivityItemProps {
  text: string;
  icon: LucideIcon;
  status?: "active" | "completed" | "warning";
}

interface TranscriptMessageProps {
  speaker: string;
  text: string;
}

const SidebarSection: React.FC<SidebarSectionProps> = ({
  title,
  children,
  className = ""
}) => (
  <div className={`${className}`}>
    <h2 className="text-lg font-medium text-gray-700 mb-5 text-left font-inter">
      {title}
    </h2>
    {children}
  </div>
);

const SuggestionItem: React.FC<SuggestionItemProps> = ({
  text,
  icon = '"'
}) => (
  <div className="flex items-start space-x-2">
    <img src={aposTropheIcon} className="w-3.5 h-3.5 mt-0.5" />
    <p className="text-black text-base font-manrope">{text}</p>
  </div>
);

const HintItem: React.FC<HintItemProps> = ({ text }) => (
  <div className="flex items-start space-x-2">
    <div className="w-1.5 h-1.5 bg-black rounded-full flex-shrink-0 mt-2"></div>
    <p className="text-black text-base font-manrope">{text}</p>
  </div>
);

const FeatureItem: React.FC<FeatureItemProps> = ({ text, enabled = true }) => (
  <div className="flex items-start space-x-2">
    <CircleCheck size={15} className={"text-black flex-shrink-0 mt-0.5"} />
    <p className={`text-base font-manrope text-black`}>{text}</p>
  </div>
);

const ActivityItem: React.FC<ActivityItemProps> = ({
  text,
  icon: Icon,
  status = "active"
}) => (
  <div className="flex items-start space-x-2">
    <Icon size={15} className="text-gray-400 flex-shrink-0 mt-0.5" />
    <p className="text-black text-base font-manrope">
      {text}
    </p>
  </div>
);

const TranscriptMessage: React.FC<TranscriptMessageProps> = ({
  speaker,
  text
}) => (
  <div
    className={`mb-4 flex ${speaker === "user" ? "justify-end" : "justify-start"}`}
  >
    <div
      className={`px-4 py-3 rounded-2xl max-w-[85%] ${
        speaker === "user"
          ? "bg-white border border-gray-200 text-gray-800 rounded-tr-md"
          : "bg-red-50 text-gray-800 rounded-tl-md"
      }`}
    >
      <p className="text-lg leading-relaxed font-manrope">{text}</p>
    </div>
  </div>
);

const TscVoicexCallDashboard: React.FC<LivekitVoiceProps> = ({
  isDialogOpen,
  toggleDialog,
  phoneNumber,
  callHints,
  callHintsTable,
  callTitle,
  agentName,
  ttsConfigId,
  voiceConfig,
  icon,
  features,
  dynamicVariables,
  isFeedbackFormOpen,
  setIsFeedbackFormOpen,
  setConnectionDetails,
  try_speaking,
  activities
}) => {
  type ConnectionDetails = {
    serverUrl: string;
    roomName: string;
    participantName: string;
    participantToken: string;
    voiceConfig: {
      tokenUrl: string;
    };
    ttsConfigId: string;
  };

  const [connectionDetails, updateConnectionDetails] = useState<
    ConnectionDetails | undefined
  >(undefined);
  const [agentState, setAgentState] = useState<AgentState>("disconnected");

  const [transcriptMessages, setTranscriptMessages] = useState<
    TranscriptMessage[]
  >([]);

  const [activeTab, setActiveTab] = useState("transcript");
  const [isCalling, setIsCalling] = useState(false);
  const [callEnded, setCallEnded] = useState<boolean>(false);
  const [currentCallId, setCurrentCallId] = useState<string | undefined>();
  const [shouldPollStatus, setShouldPollStatus] = useState(false);
  const transcriptDesktopRef = useRef<HTMLDivElement>(null);
  const transcriptMobileRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<number | null>(null);
  const [seconds, setSeconds] = useState(0);

  const { data: polledCallDetails } = useCallStatusPolling({
    baseUrl: voiceConfig.baseUrl,
    callId: currentCallId || "",
    userId: voiceConfig.user_id.toString(),
    enabled: shouldPollStatus && !!currentCallId
  });

  const tabs = [
    { id: "transcript", label: "Transcript" },
    { id: "try_speaking", label: "Try Speaking" },
    { id: "call_hints", label: "Call Hints" },
    { id: "features", label: "Features" },
    { id: "activities", label: "Activities" }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case "transcript":
        return (
          <div className="rounded-lg h-[30vh] overflow-y-auto space-y-2 scroll-smooth">
            {transcriptMessages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full">
                <div className="w-12 h-12 mb-3 rounded-full bg-red-100 flex items-center justify-center">
                  <svg
                    className="w-6 h-6 text-red-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                    />
                  </svg>
                </div>
                <h4 className="text-base font-medium text-gray-500 mb-1 text-center">
                  Your conversation with {agentName} will appear here
                </h4>
              </div>
            ) : (
              <div className="space-y-3">
                {transcriptMessages.map((message) => (
                  <TranscriptMessage
                    key={message.id}
                    speaker={message.speaker}
                    text={message.text}
                  />
                ))}
              </div>
            )}
            <div ref={transcriptMobileRef} />
          </div>
        );

      case "try_speaking":
        return (
          <div className="space-y-3 text-sm">
            {try_speaking.map((suggestion, index) => (
              <SuggestionItem key={index} text={suggestion} />
            ))}
          </div>
        );

      case "call_hints":
        return (
          <div className="space-y-3">
            {callHints.map((hint: any, index: number) => (
              <HintItem key={index} text={hint} />
            ))}
          </div>
        );

      case "activities":
        return (
          <div className="space-y-6">
            {activities.map((activity, index) => (
              <ActivityItem
                key={index}
                text={activity}
                icon={Zap}
                status="active"
              />
            ))}
          </div>
        );

      case "features":
        return (
          <div className="space-y-3">
            {features.map((feature, index) => (
              <FeatureItem key={index} text={feature} />
            ))}
          </div>
        );

      default:
        return null;
    }
  };

  const startTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    setSeconds(0);
    timerRef.current = window.setInterval(() => {
      setSeconds((prev) => prev + 1);
    }, 1000);
  };

  const stopTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const connectCall = async () => {
    const response = await fetch(voiceConfig.baseUrl + "/voice/web/call", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "user-id": voiceConfig.user_id.toString()
      },
      body: JSON.stringify({
        agent_id: voiceConfig.agent_id,
        ...(ttsConfigId && { tts_config_id: ttsConfigId }),
        overide_previous_context: voiceConfig.overide_previous_context ?? false,
        custom_dynamic_variables_config: dynamicVariables
      })
    });
    const connectionDetailsData = await response.json();

    if (!connectionDetailsData.serverUrl) {
      setCurrentCallId(connectionDetailsData.call_id);
      setShouldPollStatus(true);
    } else {
      setIsCalling(true);
      updateConnectionDetails({
        ...connectionDetailsData,
        metadata: {
          ttsConfigId: ttsConfigId,
          phoneNumber: phoneNumber
        }
      });
      startTimer();
    }
  };

  const addTranscriptMessage = (
    message: Omit<TranscriptMessage, "id" | "timestamp">
  ) => {
    const newMessage: TranscriptMessage = {
      ...message,
      id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
      timestamp: Date.now()
    };

    setTranscriptMessages((prev) => {
      const updated = [...prev, newMessage];
      return updated;
    });
  };

  useEffect(() => {
    if (isDialogOpen) {
      connectCall();
      setTranscriptMessages([]);
    }
    return () => {
      updateConnectionDetails(undefined);
      setTranscriptMessages([]);
      setShouldPollStatus(false);
      setCurrentCallId(undefined);
    };
  }, [isDialogOpen]);

  useEffect(() => {
    if (polledCallDetails && polledCallDetails.serverUrl) {
      setShouldPollStatus(false);
      setIsCalling(true);
      updateConnectionDetails({
        ...polledCallDetails,
        metadata: {
          ttsConfigId: ttsConfigId,
          phoneNumber: phoneNumber
        }
      } as any);
      startTimer();
    }
  }, [polledCallDetails, ttsConfigId, phoneNumber]);

  useEffect(() => {
    if (transcriptDesktopRef.current) {
      transcriptDesktopRef.current.scrollIntoView({ behavior: "smooth" });
    }

    if (transcriptMobileRef.current) {
      transcriptMobileRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [transcriptMessages]);

  const handleEndCall = () => {};

  return (
    <div className="min-h-screen font-manrope">
      {/* Loading overlay when polling */}
      {shouldPollStatus && !connectionDetails?.serverUrl && (
        <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50">
          <div className="text-center">
            <div className="mb-4">
              <div className="animate-pulse text-2xl font-semibold text-white">
                Finding available agent...
              </div>
            </div>
            <div className="text-gray-400">
              Please wait while we connect you
            </div>
          </div>
        </div>
      )}

      {/* @ts-ignore */}
      <LiveKitRoom
        token={connectionDetails?.participantToken}
        serverUrl={connectionDetails?.serverUrl}
        connect={connectionDetails !== undefined}
        audio={true}
        video={false}
        onMediaDeviceFailure={onDeviceFailure}
        onDisconnected={() => {
          updateConnectionDetails(undefined);
          setShouldPollStatus(false);
          setCurrentCallId(undefined);
        }}
        className="grid grid-rows-[2fr_1fr] items-center"
      >
        {/* Desktop Version - Hidden on mobile */}
        <div className="hidden sm:flex min-h-screen bg-white text-white">
          {/* Bottom gradient overlay */}
          <div className="absolute bottom-0 left-0 w-full h-[30vh] z-0 pointer-events-none bg-gradient-to-t from-red-600 to-transparent"></div>

          <div className="flex w-full relative z-10">
            {/* Left Sidebar */}
            <div className="flex-1 flex flex-col items-center pl-20">
              <div className="flex w-full justify-start mt-10">
                <button
                  className="w-14 h-14 bg-white border border-[rgba(0,0,0,0.12)] rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                  onClick={() => {
                    handleEndCall();
                    toggleDialog();
                  }}
                >
                  <X size={24} className="text-black hover:text-white" />
                </button>
              </div>

              <div className="flex flex-col mt-32 space-y-12">
                {try_speaking && try_speaking.length > 0 && (
                  <SidebarSection title="Try Speaking">
                    <div className="space-y-3">
                      {try_speaking.map((suggestion, index) => (
                        <SuggestionItem key={index} text={suggestion} />
                      ))}
                    </div>
                  </SidebarSection>
                )}

                <SidebarSection title="Call Hints">
                  <div className="space-y-3">
                    {callHints.map((hint: any, index: number) => (
                      <HintItem key={index} text={hint} />
                    ))}
                  </div>
                </SidebarSection>

                <SidebarSection title="Features">
                  <div className="space-y-3">
                    {features.map((feature, index) => (
                      <FeatureItem key={index} text={feature} />
                    ))}
                  </div>
                </SidebarSection>
              </div>
            </div>

            {/* Center widget */}
            <div className="flex-1 flex flex-col items-center justify-start mt-12 space-y-12 pr-8">
              <div className="text-center">
                <h1 className="text-3xl font-normal text-gray-700 font-inter">
                  {callTitle}
                </h1>
              </div>

              <VoiceAssistantAnimation
                isCalling={isCalling}
                setIsCalling={setIsCalling}
                onStateChange={setAgentState}
                agentName={agentName}
                disconnectCall={() => {
                  toggleDialog();
                }}
                icon={icon}
                onTranscriptMessage={addTranscriptMessage}
                startTimer={startTimer}
                stopTimer={stopTimer}
                seconds={seconds}
                callEnded={callEnded}
                setCallEnded={setCallEnded}
              />

              <RoomAudioRenderer />
              <NoAgentNotification state={agentState} />
            </div>

            {/* Right Sidebar */}
            <div className="flex-1 p-6 justify-start items-center mt-40">
              <div className="flex flex-col space-y-10 pr-24 py-">
                <SidebarSection title="Transcript" className="flex-1">
                  <div
                    className="flex flex-col bg-white/80 p-4 h-96 overflow-y-auto space-y-2 scroll-smooth text-gray-300"
                  >
                    {transcriptMessages.length === 0 ? (
                      <div className="flex flex-col items-center justify-center h-full">
                        <div className="w-16 h-16 mb-4 rounded-full bg-red-300 flex items-center justify-center">
                          <svg
                            className="w-8 h-8 text-white"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={1.5}
                              d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                            />
                          </svg>
                        </div>
                        <h4 className="text-xl font-medium text-gray-400 mb-2 text-center">
                          Your conversation with {agentName} will appear here
                        </h4>
                      </div>
                    ) : (
                      <>
                        {transcriptMessages.map((message) => (
                          <TranscriptMessage
                            key={message.id}
                            speaker={message.speaker}
                            text={message.text}
                          />
                        ))}
                      </>
                    )}
                    <div ref={transcriptDesktopRef} />
                  </div>
                </SidebarSection>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Version - Hidden on desktop */}
        <div className="flex sm:hidden min-h-screen bg-white text-black flex-col">
          <div className="flex items-center p-4">
            <button
              className="w-10 h-10 rounded-full flex items-center justify-center bg-red-500 transition-colors"
              onClick={() => {
                handleEndCall();
                toggleDialog();
              }}
            >
              <X size={20} className="text-white" />
            </button>

            <h1 className="text-lg font-normal text-gray-800 font-inter ml-8">
              {callTitle}
            </h1>
          </div>

          {/* Voice Call Interface */}
          <div className="flex justify-center mx-auto">
            <VoiceAssistantAnimation
              isCalling={isCalling}
              setIsCalling={setIsCalling}
              onStateChange={setAgentState}
              agentName={agentName}
              disconnectCall={() => {
                toggleDialog();
              }}
              icon={icon}
              onTranscriptMessage={addTranscriptMessage}
              startTimer={startTimer}
              stopTimer={stopTimer}
              seconds={seconds}
              callEnded={callEnded}
              setCallEnded={setCallEnded}
            />
            <NoAgentNotification state={agentState} />
          </div>

          {/* Tab Navigation */}
          <div className="flex-shrink-0 px-12">
            <div className="flex overflow-x-auto scrollbar-hide">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-shrink-0 px-4 py-3 text-sm font-medium transition-colors relative ${
                    activeTab === tab.id
                      ? "text-black"
                      : "text-gray-400 hover:text-gray-700"
                  }`}
                >
                  {tab.label}
                  {activeTab === tab.id && (
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-black" />
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div className="flex-1 w-[100vw] p-4 justify-center">
            {renderTabContent()}
          </div>
        </div>
      </LiveKitRoom>
    </div>
  );
};

function VoiceAssistantAnimation({
  isCalling,
  setIsCalling,
  onStateChange,
  agentName,
  disconnectCall,
  onTranscriptMessage,
  icon,
  startTimer,
  stopTimer,
  seconds,
  callEnded,
  setCallEnded
}: {
  isCalling: boolean;
  setIsCalling: Function;
  onStateChange: Function;
  agentName: string;
  disconnectCall: Function;
  icon: string;
  onTranscriptMessage: (
    message: Omit<TranscriptMessage, "id" | "timestamp">
  ) => void;
  startTimer: Function;
  stopTimer: Function;
  seconds: number;
  callEnded: boolean;
  setCallEnded: Function;
}) {
  async function waitForConnected(room: Room) {
    if (room.state === "connected") return;
    await once(room, RoomEvent.Connected);
  }

  const { state } = useVoiceAssistant();
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(
    null
  );
  const audioContextRef = useRef<AudioContext | null>(null);
  const [isMuted, setIsMuted] = useState(false);
  const room = useRoomContext();
  const { localParticipant } = useRoomContext();
  const handleChat = () => {};

  const formatTime = (timestamp: number) => {
    return new Date(timestamp).toLocaleTimeString("en-US", {
      hour12: false,
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit"
    });
  };

  const endCall = async () => {
    if (!isCalling) return;
    await room.disconnect();
    stopTimer();
    setIsCalling(false);
    setCallEnded(true);
  };

  useEffect(() => {
    waitForConnected(room);

    const startRecording = async () => {
      try {
        if (localParticipant) {
          const stream = await navigator.mediaDevices.getUserMedia({
            audio: true
          });

          audioContextRef.current = new AudioContext();
          const source =
            audioContextRef.current.createMediaStreamSource(stream);

          const highPassFilter = audioContextRef.current.createBiquadFilter();
          highPassFilter.type = "highpass";
          highPassFilter.frequency.setValueAtTime(
            400,
            audioContextRef.current.currentTime
          );

          source.connect(highPassFilter);
          const destination =
            audioContextRef.current.createMediaStreamDestination();
          highPassFilter.connect(destination);

          const mediaRecorder = new MediaRecorder(destination.stream);
          setMediaRecorder(mediaRecorder);
          mediaRecorder.start();

          mediaRecorder.onerror = (event) => {
            console.error("MediaRecorder error:", (event as ErrorEvent).error);
          };
        }

        /* @ts-ignore */
        room.on(RoomEvent.DataReceived, (payload, participant, kind) => {
          try {
            const message = new TextDecoder().decode(payload);

            let parsedMessage;
            try {
              parsedMessage = JSON.parse(message);
            } catch (parseError) {
              if (message && message.trim()) {
                const currentTime = Date.now();
                onTranscriptMessage({
                  speaker: "agent",
                  text: message.trim(),
                  time: formatTime(currentTime)
                });
              }
              return;
            }

            if (parsedMessage && parsedMessage.content && !parsedMessage.type) {
              const currentTime = Date.now();
              const speaker = parsedMessage.speaker;

              onTranscriptMessage({
                speaker: speaker,
                text: parsedMessage.content,
                time: formatTime(currentTime),
                type: parsedMessage.type || undefined
              });
            } else if (parsedMessage && parsedMessage.text) {
              const currentTime = Date.now();
              onTranscriptMessage({
                speaker: parsedMessage.speaker || "agent",
                text: parsedMessage.content,
                time: formatTime(currentTime),
                type: parsedMessage.type
              });
            }
          } catch (error) {
            console.error("Error processing transcript data:", error);
          }
        });

        room.on(RoomEvent.Disconnected, (reason) => {
          stopTimer();
          setIsCalling(false);
          setCallEnded(true);
        });
      } catch (error) {
        console.error("Failed to access microphone:", error);
      }
    };

    if (room) {
      startRecording();
    }

    return () => {
      if (
        audioContextRef.current &&
        audioContextRef.current.state !== "closed"
      ) {
        audioContextRef.current.close();
      }
      if (mediaRecorder && mediaRecorder.state !== "inactive") {
        mediaRecorder.stop();
      }
      stopTimer();
    };
  }, []);

  useEffect(() => {
    onStateChange(state);
  }, [state]);

  const toggleMute = useCallback(async () => {
    await waitForConnected(room);

    if (!localParticipant) {
      alert("Agent Disconnected. Please Wait!");
      return;
    }

    const newEnabledState = !localParticipant.isMicrophoneEnabled;
    await localParticipant.setMicrophoneEnabled(newEnabledState);

    setIsMuted(!newEnabledState);
  }, [room, localParticipant]);

  return (
    <>
      <VoiceCallInterface
        isCalling={isCalling}
        isMuted={isMuted}
        setIsMuted={setIsMuted}
        toggleMute={toggleMute}
        agentName={agentName}
        onEndCall={endCall}
        onChat={handleChat}
        mediaRecorder={mediaRecorder}
        icon={TSCAgent}
      />
    </>
  );
}

function onDeviceFailure(error?: MediaDeviceFailure) {
  console.error(error);
  alert(
    "Error acquiring camera or microphone permissions. Please make sure you grant the necessary permissions in your browser and reload the tab"
  );
}

export default TscVoicexCallDashboard;
